/**
 * Shared Components for Mission, Vision and Values Sections
 * Provides reusable components for displaying organization's mission, vision, and core values.
 * Used in both Welcome and About pages.
 *
 * Author: Meng Li
 * Created on: 2025-01-13
 * Last updated on: 2025-01-14
 * Professor: Mejdi Eraslan
 * Client: Melisa Eraslan
 */

import React from 'react';
import './MissionVisionValues.css';

// import Image assets
import accessibility from '../images/Welcome/clipart_accessibility.png';
import empowerment from '../images/Welcome/clipart_empowerment.png';
import compassion from '../images/Welcome/clipart_compassion.png';

export const MissionVisionSection = () => {
    const Introduction = () => (
        <div className="mission-vision-intro">
            <h2 className="h2 title-text">Mission and Vision</h2>
            <p className="p card-text">
                We believe that communities shape and are shaped by the engagement, inclusion, and
                occupations of persons who live in or near the margin. We are working towards having stronger
                and more inclusive communities by igniting our sense of togetherness.
            </p>
        </div>
    );

    const MissionStatement = () => (
        <div className="mission-statement">
            <h3 className="h3 title-text">Mission</h3>
            <p className="p card-text">
                At SimpliCITY, our mission is to simplify
                access to essential services for Montreal
                and Ottawa's homeless population.
                <p className="p new-line"></p>
                By fostering empowerment and ease
                of access throughout the city, we aim to
                improve the lives of those in need.
            </p>
        </div>
    );

    const VisionStatement = () => (
        <div className="vision-statement">
            <h3 className="h3 title-text">Vision</h3>
            <p className="p card-text">
                Our vision is that increasing resource and service accessibility and awareness will lead to greater
                visibility, better health outcomes, increased, quality of life, increased housing opportunities
                and an increased sense of community and feelings of belonging.
            </p>
        </div>
    );

    return (
        <>
            <Introduction />
            <div className="mission-vision-container">
                <MissionStatement />
                <VisionStatement />
            </div>
        </>
    );
};

export const ValuesSection = () => {
    const ValuesBanner = () => (
        <div className="values-banner">
            <h2 className="h2 banner-text">Our Values</h2>
        </div>
    );

    const ValueCard = ({ icon, title, description }) => (
        <div className="value-card">
            <div className="image-circle">
                <img src={icon} alt={`${title} icon`}/>
            </div>
            <h3 className="h3 title-text">{title}</h3>
            <p className="p card-text">{description}</p>
        </div>
    );

    const VALUES_DATA = [
        {
            icon: accessibility,
            title: "Accessibility",
            description: "We are committed to ensuring that essential services are accessible to all of those in need."
        },
        {
            icon: empowerment,
            title: "Empowerment",
            description: "We empower individuals by simplifying access to essential services, enabling them to take control of their circumstances."
        },
        {
            icon: compassion,
            title: "Compassion",
            description: "Our work is driven by compassion and empathy, recognizing the humanity and dignity of every individual we serve."
        }
    ];

    return (
        <>
            <ValuesBanner />
            <div className="values-grid">
                {VALUES_DATA.map((value, index) => (
                    <ValueCard key={index} {...value} />
                ))}
            </div>
        </>
    );
};
